import { Divider } from "antd-v5";
import FilterableCell from "components/FilterableCell";
import FindingRiskScoreTag from "components/FindingRiskScorePopover";
import { CircleCheckGreenIcon } from "components/Icons";
import InternalLink from "components/InternalLink";
import { RichTextRenderer } from "components/RichTextRenderer";
import User from "components/User";
import { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import dayjs from "dayjs";
import _, { compact, find, isEmpty, isFunction, isUndefined } from "lodash";
import React, { Key, MouseEvent } from "react";
import { FindingSummary } from "types/api/findingSummary";
import { NameIdPair } from "types/api/nameIdPair";
import { DateFormatType } from "types/common";
import {
  FilterChangeCallBack,
  FilterInfo,
  FilterValue,
} from "types/pagination";
import {
  ArmorcodeCustomField,
  CustomFindingColumn,
  DerivedAttribute,
} from "types/tenantConfig";
import { hasPermission } from "utils/authService";
import { getCRADefaultStatus } from "utils/craComplianceUtils";
import { getRiskRatingFromRiskScore } from "utils/riskScoreUtil";
import { CellRenderers, formatNumber } from "utils/tableUtils";
import {
  DEFAULT_DATE_FORMATS,
  FINDING_STATUS,
  FINDING_STATUS_DISPLAY_TEXT,
  FINDING_STATUS_VOCABULARY_MAP,
  GroupBySupportedFields,
  SLA_STATUS_FILTERS,
  TAG_GROUP_DISABLE_MESSAGE,
} from "views/Findings/Constants";
import EPSSScore from "views/Findings/EPSSScore";
import FindingAssetScoreFormula from "views/Findings/FindingDetails/FindingAssetScoreFormula";
import FindingScoreFormula from "views/Findings/FindingDetails/FindingScoreFormula";
import FindingIdCell from "views/Findings/FindingIdCell";
import { correlationNameRenderer } from "views/Findings/FindingList/FindingListGroupByTable";
import { renderBooleanValues } from "views/Findings/FindingsListV3";
import TrimText from "views/Findings/TrimText";
import {
  getImageName,
  isTagFilterApplied,
  renderMultipleTicketEntriesAsRows,
} from "views/Findings/utils";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";

import ACLink from "ac-components/ACLink/ACLink";
import ACSeverityTag, { SeverityType } from "ac-components/ACSeverityTag";
import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACFilterableCell from "ac-components/ac-filterable-cell/ACFilterableCell";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import ACRiskRatingTag, {
  RiskRating,
} from "ac-components/ac-risk-rating-tag/ACRiskRatingTag";
import ACRiskScoreTag from "ac-components/ac-riskscore-tag/ACRiskScoreTag";
import ACTag from "ac-components/ac-tag/ACTag";

import useCustomColumns from "./useCustomColumns";
import useCustomFieldsColumns from "./useCustomFieldsColumns";
import useDateFormats from "./useDateFormats";
import useDerivedAttributesColumns from "./useDerivedAttributesColumns";
import useResolvedFindingStatus from "./useResolvedFindingStatus";
import { renderProductSubproductLink } from "./useScanColumns";

type FindingColumns = ACTableColumnType<FindingSummary>[];
type ShowFindingDetail = (e: MouseEvent<HTMLAnchorElement>) => void;

export const getFindingIdColumn = ({
  showFindingDetail,
}: {
  showFindingDetail: ShowFindingDetail;
}): ACTableColumnType<FindingSummary> => {
  return {
    title: "ID",
    dataIndex: "id",
    key: "id",
    sorter: true,
    render: (id: string, record, index) => (
      <FindingIdCell
        id={id}
        showFindingDetail={showFindingDetail}
        index={index}
      />
    ),
    fixed: "left",
    width: 120,
    sortType: "number",
  };
};

export default function useFindingColumns({
  showFindingDetail,
  externalColumn,
  filterInfo,
  onFilterChange,
  isAQLActive,
  columnInitialWidths,
  findingCustomFields,
  derivedAttributes,
}: {
  showFindingDetail: ShowFindingDetail;
  externalColumn: CustomFindingColumn[];
  filterInfo?: FilterInfo;
  onFilterChange?: FilterChangeCallBack;
  isAQLActive?: boolean;
  columnInitialWidths?: Record<string, number>;
  findingCustomFields?: ArmorcodeCustomField[];
  derivedAttributes?: DerivedAttribute[];
}): FindingColumns {
  const dateFormatter = useDateFormats({
    prefixKey: "finding",
    defaultToKeys: DEFAULT_DATE_FORMATS as Record<Key, DateFormatType>,
  });
  const featureFlags = useFeatureFlag();
  const resolvedFindingStatus = useResolvedFindingStatus();
  const globalSettings = useGlobalSettingsContext();
  const customColumns = useCustomColumns<FindingSummary>({ externalColumn });
  const customFieldColumns = useCustomFieldsColumns(findingCustomFields);
  const derivedAttributesColumns =
    useDerivedAttributesColumns(derivedAttributes);

  const applyCellFilter =
    !isAQLActive && onFilterChange
      ? (key: React.Key, value: FilterValue) => {
          if (onFilterChange) {
            onFilterChange({ ...filterInfo, [key]: [value] });
          }
        }
      : undefined;

  const getApplyCellFilter = (
    applyCellFilter: ((key: string, value: FilterValue) => void) | undefined,
    key: string,
    value: FilterValue,
  ) => (applyCellFilter ? () => applyCellFilter(key, value) : undefined);

  const columns: FindingColumns = [
    getFindingIdColumn({ showFindingDetail }),
    ...(featureFlags.RISK_SCORE_3_0
      ? ([
          {
            title: `${getVocabulary("riskScore")}`,
            dataIndex: "riskScore",
            key: "riskScore",
            width: 140,
            render: (riskScore, finding) => {
              // If the finding is resolved i.e Mitigated, remediated, false+ve, accept risk or severity is INFO, we should not show any calculation logic tooltip, the scores may not match with the current state of the finding
              const isStatusResolvedOrInfoSeverity =
                resolvedFindingStatus.includes(finding?.status ?? "") ||
                finding?.severity === "INFO";
              const riskScoreTag = (
                <ACRiskScoreTag value={finding?.riskScore} />
              );

              // @ts-ignore
              return !globalSettings.riskScoreEnabled ||
                isStatusResolvedOrInfoSeverity ? (
                riskScoreTag
              ) : (
                <FindingRiskScoreTag
                  finding={finding}
                  fetchFinding={undefined}
                  showFindingLink={undefined}
                >
                  {riskScoreTag}
                </FindingRiskScoreTag>
              );
            },
            sorter: true,
            sortType: "number",
          },
          {
            title: "Risk Rating",
            dataIndex: "riskScore",
            key: "riskRating",
            width: 140,
            render: (riskScore) => {
              if (riskScore == null || !globalSettings.riskScoreEnabled) {
                return <ACRiskScoreTag value={riskScore} />;
              }
              const riskRating = getRiskRatingFromRiskScore(
                riskScore,
                globalSettings.riskScoreRange,
              );
              return <ACRiskRatingTag rating={riskRating as RiskRating} />;
            },
            sorter: true,
          },
        ] as FindingColumns)
      : []),
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (description) => <RichTextRenderer content={description} />,
    },
    {
      title: getVocabulary("stepToReproduce"),
      dataIndex: "stepsToReproduce",
      key: "stepsToReproduce",
      render: (stepsToReproduce) => (
        <RichTextRenderer content={stepsToReproduce} />
      ),
      width: 300,
    },
    {
      title: getVocabulary("impact"),
      dataIndex: "impact",
      key: "impact",
      render: (impact) => <RichTextRenderer content={impact} />,
      width: 300,
    },
    {
      title: getVocabulary("mitigation"),
      dataIndex: "mitigation",
      key: "mitigation",
      render: (mitigation) => <RichTextRenderer content={mitigation} />,
      width: 300,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      sorter: true,
      render: (status: string) => {
        const displayText = FINDING_STATUS_DISPLAY_TEXT[status] || status;
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "status",
              status,
            )}
            isAQLApplied={isAQLActive}
          >
            <div title={getVocabulary(FINDING_STATUS_VOCABULARY_MAP[status])}>
              {displayText}
            </div>
          </ACFilterableCell>
        );
      },
    },
    {
      title: "Resurface Date",
      dataIndex: "unsupressTime",
      key: "unsupressTime",
      width: 200,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("unsupressTime", value),
      dateFormatter: dateFormatter,
    },
    {
      title: "Summary",
      dataIndex: "title",
      key: "title",
      width: columnInitialWidths?.title ?? 300,
      sorter: true,
    },
    {
      title: getVocabulary("severity"),
      dataIndex: "severity",
      key: "severity",
      sorter: true,
      sortType: "severity",
      width: 120,
      render: (severity: string) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "severity",
              _.capitalize(severity),
            )}
            isAQLApplied={isAQLActive}
          >
            <ACSeverityTag value={severity as SeverityType} />
          </ACFilterableCell>
        );
      },
    },
    ...(featureFlags.AATI_SCORE_COLUMN
      ? ([
          {
            title: "AATI Score",
            dataIndex: "aatiScore",
            key: "aatiScore",
            width: 130,
            render: (text, record) =>
              CellRenderers.VALUE_OR_NA(record.additionalDetails?.aatiScore),
            sorter: true,
            sortType: "number",
          },
        ] as FindingColumns)
      : []),
    {
      title: `${getVocabulary("findingScore")}`,
      dataIndex: "findingScore",
      key: "findingScore",
      width: 140,
      render: (findingScore, finding) => (
        <FindingScoreFormula
          findingScore={findingScore}
          severity={finding.severity}
          status={finding.status}
          additionalDetails={finding.additionalDetails}
          cvssBaseScore={finding.baseScore}
          cves={finding.cve}
          aatiScore={finding.additionalDetails?.aatiScore}
          toolName={finding.source}
        />
      ),
      sorter: true,
      sortType: "number",
    },
    {
      title: "CVE",
      dataIndex: "cve",
      key: "cve",
      width: 190,
      render: (cves: string[] | undefined, record) => (
        <ACResponsiveTags
          tags={compact(cves)}
          itemRenderer={(cve) => renderCVELink(cve, record.id!)}
          onFilterClick={
            applyCellFilter
              ? (cve) => {
                  applyCellFilter("cve", cve);
                }
              : undefined
          }
        />
      ),
      sorter: true,
    },
    {
      title: `CVE Last Modified Date`,
      dataIndex: "lastModifiedDate",
      key: "lastModifiedDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastModifiedDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `CVE Published Date`,
      dataIndex: "publishedDate",
      key: "publishedDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("publishedDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: "Scan ID",
      dataIndex: "scan",
      key: "scan",
      width: 110,
      sorter: true,
      sortType: "number",
      render: (text) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(applyCellFilter, "scan", text)}
            isAQLApplied={isAQLActive}
          >
            {`S-${text}`}
          </ACFilterableCell>
        );
      },
    },
    {
      title: "Base Score",
      dataIndex: "baseScore",
      key: "baseScore",
      width: 123,
      sorter: true,
      sortType: "number",
      render: CellRenderers.VALUE_OR_NA,
    },

    {
      title: "Overall Score",
      dataIndex: "cvssOverallScore",
      key: "cvssOverallScore",
      width: 130,
      sorter: true,
      sortType: "number",
      render: (cvssOverallScore, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.cvssOverallScore),
    },

    {
      title: "Exploitability Score",
      dataIndex: "exploitabilityScore",
      key: "exploitabilityScore",
      width: 160,
      sorter: true,
      sortType: "number",
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "Impact Score",
      dataIndex: "impactScore",
      key: "impactScore",
      width: 130,
      sorter: true,
      sortType: "number",
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "CWE",
      dataIndex: "cwesStrings",
      key: "cwesStrings",
      width: 140,
      render: (tags: string[] | undefined) => (
        <ACResponsiveTags
          tags={tags}
          itemRenderer={renderCWELink}
          onFilterClick={
            applyCellFilter
              ? (cwe) => {
                  applyCellFilter("cwesStrings", cwe);
                }
              : undefined
          }
        />
      ),
    },
    {
      title: "Finding Age",
      dataIndex: "foundOnDate",
      key: "foundOnDate",
      width: 130,
      sorter: true,
      sortType: "date",
      render: CellRenderers.AGE,
    },
    {
      title: "Found On",
      dataIndex: "foundOn",
      key: "foundOn",
      width: 150,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("foundOn", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `${getVocabulary("resolution")} Due Date`,
      dataIndex: "slaDueDate",
      key: "slaDueDate",
      width: 130,
      sorter: true,
      sortType: "date",
      render: (value) =>
        globalSettings.isSLAEnabled
          ? dateFormatter.render("slaDueDate", value)
          : NOT_AVAILABLE,
    },
    {
      title: `${getVocabulary("resolution")} Due Date Note`,
      dataIndex: "lastResolutionNote",
      key: "lastResolutionNote",
      width: 130,
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "Latest Tool Scan Date",
      dataIndex: "lastScanDate",
      key: "lastScanDate",
      width: 130,
      render: (value: number) => dateFormatter.render("lastScanDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("confirmed")} Date`,
      dataIndex: "lastConfirmDate",
      key: "lastConfirmDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastConfirmDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("falsePositive")} Date`,
      dataIndex: "lastFalsePositiveDate",
      key: "lastFalsePositiveDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastFalsePositiveDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("open")} Date`,
      dataIndex: "lastOpenDate",
      key: "lastOpenDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastOpenDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("acceptRisk")} Date`,
      dataIndex: "lastAcceptRiskDate",
      key: "lastAcceptRiskDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastAcceptRiskDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("suppressed")} Date`,
      dataIndex: "lastSuppressDate",
      key: "lastSuppressDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastSuppressDate", value),
      dateFormatter: dateFormatter,
    },
    ...(!globalSettings.disabledFindingStatus.includes(
      FINDING_STATUS.CONTROLLED,
    )
      ? ([
          {
            title: `Last ${getVocabulary("controlledStatus")} Date`,
            dataIndex: "lastControlledDateTime",
            key: "lastControlledDateTime",
            width: 200,
            sorter: true,
            sortType: "date",
            render: (value) =>
              dateFormatter.render("lastControlledDateTime", value),
            dateFormatter: dateFormatter,
          },
        ] as FindingColumns)
      : []),
    ...(!globalSettings.disabledFindingStatus.includes(
      FINDING_STATUS.IN_PROGRESS,
    )
      ? ([
          {
            title: `Last ${getVocabulary("inProgressStatus")} Date`,
            dataIndex: "lastInProgressDateTime",
            key: "lastInProgressDateTime",
            width: 200,
            sorter: true,
            sortType: "date",
            render: (value) =>
              dateFormatter.render("lastInProgressDateTime", value),
            dateFormatter: dateFormatter,
          },
        ] as FindingColumns)
      : []),
    {
      title: getVocabulary("product"),
      dataIndex: "product",
      key: "product",
      render: (product) => {
        return product?.id ? (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "product",
              product.id,
            )}
            isAQLApplied={isAQLActive}
          >
            {renderProductSubproductLink(product, "/product")}
          </ACFilterableCell>
        ) : null;
      },
      width: 176,
    },
    {
      title: `${getVocabulary("product")} Status`,
      dataIndex: "productStatus",
      key: "productStatus",
      width: 130,
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: getVocabulary("subProduct"),
      dataIndex: "subProduct",
      key: "subProduct",
      render: (subProduct) => {
        return subProduct?.id ? (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "subProduct",
              subProduct.id,
            )}
            isAQLApplied={isAQLActive}
          >
            {renderProductSubproductLink(subProduct, "/subProduct")}
          </ACFilterableCell>
        ) : null;
      },
      width: 176,
    },
    {
      title: `${getVocabulary("subProduct")} Version`,
      dataIndex: "subProductVersion",
      key: "subProductVersion",
      render: CellRenderers.VALUE_OR_NA,
      width: 130,
    },
    {
      title: `${getVocabulary("subProduct")} Status`,
      dataIndex: "subProductStatuses",
      key: "subProductStatuses",
      width: 130,
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "Environment",
      dataIndex: "environmentName",
      key: "environmentName",
      render: (text, record) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "environmentName",
              record.environment?.name as FilterValue,
            )}
            isAQLApplied={isAQLActive}
          >
            <EnvironmentName name={record.environment} isToolTip={true} />
          </ACFilterableCell>
        );
      },
      width: 176,
      sorter: true,
    },
    {
      title: getVocabulary("team"),
      dataIndex: "team",
      key: "team",
      render: (team) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "team",
              team?.id,
            )}
          >
            {CellRenderers.NAME_ID_MAP(team)}
          </ACFilterableCell>
        );
      },
      width: 120,
    },
    {
      title: getVocabulary("owner"),
      dataIndex: "owner",
      key: "owner",
      render: (owner) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "owner",
              owner?.id,
            )}
            isAQLApplied={isAQLActive}
          >
            {CellRenderers.NAME_ID_MAP(owner)}
          </ACFilterableCell>
        );
      },
      width: 160,
    },
    {
      title: getVocabulary("businessOwner"),
      dataIndex: "businessOwner",
      key: "businessOwner",
      render: (businessOwner) => businessOwner?.name || NOT_AVAILABLE,
      width: 120,
    },
    {
      title: getVocabulary("securityOwner"),
      dataIndex: "securityOwner",
      key: "securityOwner",
      render: (securityOwner) => securityOwner?.name || NOT_AVAILABLE,
      width: 120,
    },
    {
      title: getVocabulary("engineeringOwner"),
      dataIndex: "engineeringOwner",
      key: "engineeringOwner",
      render: (engineeringOwner) => engineeringOwner?.name || NOT_AVAILABLE,
      width: 120,
    },
    {
      title: getVocabulary("supportOwner"),
      dataIndex: "supportOwner",
      key: "supportOwner",
      render: (supportOwner) => supportOwner?.name || NOT_AVAILABLE,
      width: 120,
    },
    {
      title: getVocabulary("complianceOwner"),
      dataIndex: "complianceOwner",
      key: "complianceOwner",
      render: (complianceOwner) => complianceOwner?.name || NOT_AVAILABLE,
      width: 120,
    },
    {
      title: `${getVocabulary("project")}`,
      dataIndex: "armorcodeProjects",
      key: "armorcodeProjects",
      width: 130,
      render: (p, record) => {
        return (
          <ACResponsiveTags
            tags={record.armorcodeProjects?.map((p) => p.name!)}
            itemRenderer={(project) => {
              const projectId = record?.armorcodeProjects?.find(
                (p) => p.name === project,
              )?.id;
              return (
                <InternalLink
                  to={`/project/${projectId}`}
                  component={(props) => (
                    <ACLink
                      {...props}
                      primary={false}
                      overlay
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                >
                  {project}
                </InternalLink>
              );
            }}
            onFilterClick={
              applyCellFilter
                ? (name, index) => {
                    applyCellFilter(
                      "armorcodeProjects",
                      record.armorcodeProjects?.[index!].id ?? "",
                    );
                  }
                : undefined
            }
          />
        );
      },
    },
    ...(featureFlags.EXCEPTION_MODULE &&
    hasPermission(PermissionSetMap.READ_RISK_REGISTER)
      ? ([
          {
            title: `${getVocabulary("exception")} Name`,
            dataIndex: "exceptions",
            key: "riskRegister",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.map(
                record.exceptions,
                (data) => data.name,
              );
              return renderException(
                exceptionData,
                record,
                applyCellFilter,
                true,
              );
            },
          },
          {
            title: `${getVocabulary("exception")} ID`,
            dataIndex: "exceptions",
            key: "exceptionsId",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.map(record.exceptions, (data) => data.id);
              //@ts-expect-error
              return renderException(exceptionData, record, false, true);
            },
          },
          {
            title: `${getVocabulary("exception")} Start Date`,
            dataIndex: "exceptions",
            key: "exceptionsStartDate",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.map(
                record.exceptions,
                (data) => data.startDate,
              );
              return renderException(exceptionData, record, false, false, true);
            },
          },
          {
            title: `${getVocabulary("exception")} End Date`,
            dataIndex: "exceptions",
            key: "exceptionsEndDate",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.map(
                record.exceptions,
                (data) => data.endDate,
              );
              return renderException(exceptionData, record, false, false, true);
            },
          },
          {
            title: `${getVocabulary("exception")} Reason`,
            dataIndex: "exceptions",
            key: "exceptionsReason",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.flatMap(
                record.exceptions,
                (data) => data.reasons,
              );
              return renderException(exceptionData, record);
            },
          },
          {
            title: `${getVocabulary("exception")} Status`,
            dataIndex: "exceptions",
            key: "exceptionsStatus",
            width: 130,
            render: (p, record) => {
              const exceptionData = _.map(record.exceptions, (data) =>
                _.capitalize(data.status),
              );
              return renderException(exceptionData, record);
            },
          },
        ] as FindingColumns)
      : []),
    ...(featureFlags.PENTEST_MODULE
      ? [
          {
            title: getVocabulary("assessment"),
            dataIndex: "assessments",
            key: "assessments",
            render: (assessments: NameIdPair[]) => {
              return !_.isEmpty(assessments)
                ? _.map(assessments, (assessment) => (
                    <ACFilterableCell
                      applyCellFilter={getApplyCellFilter(
                        applyCellFilter,
                        "assessments",
                        assessment.id!,
                      )}
                      isAQLApplied={isAQLActive}
                    >
                      {renderProductSubproductLink(assessment, "/assessments")}
                    </ACFilterableCell>
                  ))
                : CellRenderers.VALUE_OR_NA();
            },
            width: 176,
          },
        ]
      : []),
    {
      title: "Ticket ID",
      dataIndex: "armorcodeTicket",
      key: "armorcodeTicket",
      render: (armorcodeTicket, record) => {
        if (isEmpty(record.armorcodeTicket)) {
          return CellRenderers.VALUE_OR_NA();
        }
        return record.armorcodeTicket!.map((entry, index) => {
          const {
            isArmorcodeTicketClosed,
            armorcodeTicketUrl,
            armorcodeTicketId,
          } = entry;

          return (
            <div key={index}>
              <ACTag
                label={armorcodeTicketId!}
                onFilterClick={
                  applyCellFilter
                    ? (value) => {
                        applyCellFilter("armorcodeTicket", value);
                      }
                    : undefined
                }
              >
                {isArmorcodeTicketClosed ? (
                  <CircleCheckGreenIcon className="m-r-xs" />
                ) : null}
                <ACLink
                  primary={false}
                  href={armorcodeTicketUrl}
                  target="_blank"
                >
                  {armorcodeTicketId}
                </ACLink>
              </ACTag>
              {index !== record.armorcodeTicket!.length - 1 ? (
                <Divider className="m-y-xs" />
              ) : null}
            </div>
          );
        });
      },
      sorter: true,
      width: 130,
    },
    {
      title: "Ticket Status",
      dataIndex: "armorcodeTicketStatus",
      key: "armorcodeTicketStatus",
      render: (_, record) => {
        const ticketStatus = record.armorcodeTicket?.[0]?.armorcodeTicketStatus;
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "armorcodeTicketStatus",
              ticketStatus as FilterValue,
            )}
            isAQLApplied={isAQLActive}
          >
            {renderMultipleTicketEntriesAsRows(
              record.armorcodeTicket,
              "armorcodeTicketStatus",
            )}
          </ACFilterableCell>
        );
      },
      width: 130,
    },
    {
      title: "Source Tool",
      dataIndex: "source",
      key: "source",
      width: 160,
      sorter: true,
      render: (source) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "source",
              source,
            )}
            isAQLApplied={isAQLActive}
          >
            {CellRenderers.VALUE_OR_NA(source)}
          </ACFilterableCell>
        );
      },
    },
    {
      title: "Scan Type",
      dataIndex: "scanType",
      key: "scanType",
      render: (_, record) => {
        const scanTypeValue = CellRenderers.VALUE_OR_NA(record.scanType?.[0]);
        const filterValue = scanTypeValue === "N/A" ? null : scanTypeValue;
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "scanType",
              filterValue,
            )}
            isAQLApplied={isAQLActive}
          >
            {scanTypeValue === "N/A" ? (
              scanTypeValue
            ) : (
              <ACTag label={scanTypeValue} />
            )}
          </ACFilterableCell>
        );
      },
      width: 120,
      sorter: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      render: CellRenderers.VALUE_OR_NA,
      sorter: true,
      width: 110,
    },
    {
      title: "Tool Finding ID",
      dataIndex: "toolId",
      key: "toolId",
      render: (value, record) => {
        value = CellRenderers.VALUE_OR_NA(value);
        if (value === CellRenderers.VALUE_OR_NA()) {
          return value;
        }
        return (
          <FilterableCell
            showFilter={!isAQLActive}
            filterKey="toolId"
            value={value}
            className={undefined}
            getFilterValue={undefined}
          >
            {record.url ? (
              <ACLink href={record.url} target="_blank" title={value}>
                {value}
              </ACLink>
            ) : (
              value
            )}
          </FilterableCell>
        );
      },
      width: 150,
      sorter: true,
    },
    {
      title: "Tool Finding URL",
      dataIndex: "url",
      key: "url",
      render: (value) => {
        if (!value) {
          return CellRenderers.VALUE_OR_NA();
        }
        return (
          <ACLink href={value} target="_blank" title={value}>
            {value}
          </ACLink>
        );
      },
      width: 300,
    },
    {
      title: "Tool Severity",
      dataIndex: "toolSeverity",
      key: "toolSeverity",
      render: (severity: string) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "toolSeverity",
              _.capitalize(severity),
            )}
            isAQLApplied={isAQLActive}
          >
            <ACSeverityTag
              value={severity as SeverityType}
              isCustomizedValue={false}
            />
          </ACFilterableCell>
        );
      },
      sorter: true,
      sortType: "severity",
      width: 180,
    },
    {
      title: "Tool Finding Status",
      dataIndex: "toolFindingStatus",
      key: "toolFindingStatus",
      render: (_, record) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "toolFindingStatus",
              record.additionalDetails?.toolStatusOriginal as FilterValue,
            )}
            isAQLApplied={isAQLActive}
          >
            {CellRenderers.VALUE_OR_NA(
              record.additionalDetails?.toolStatusOriginal,
            )}
          </ACFilterableCell>
        );
      },
      width: 180,
      sorter: true,
    },
    {
      title: `Tool Finding ${getVocabulary("category")}`,
      dataIndex: "findingCategory",
      key: "findingCategory",
      render: (findingCategory: string) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "findingCategory",
              findingCategory,
            )}
            isAQLApplied={isAQLActive}
          >
            {CellRenderers.VALUE_OR_NA(findingCategory)}
          </ACFilterableCell>
        );
      },
      width: 160,
      sorter: true,
    },
    {
      title: getVocabulary("category"),
      dataIndex: "armorcodeCategory",
      key: "armorcodeCategory",
      render: (armorcodeCategory: string) => {
        return (
          <ACFilterableCell
            applyCellFilter={getApplyCellFilter(
              applyCellFilter,
              "armorcodeCategory",
              armorcodeCategory,
            )}
            isAQLApplied={isAQLActive}
          >
            {CellRenderers.VALUE_OR_NA(armorcodeCategory)}
          </ACFilterableCell>
        );
      },
      width: 150,
      sorter: true,
    },
    {
      title: "Tags",
      dataIndex: "keyValue",
      key: "keyValue",
      groupByKey: "tags",
      allowGroupBy: () => ({
        disabled: !isTagFilterApplied(filterInfo),
        reason: TAG_GROUP_DISABLE_MESSAGE,
      }),
      width: 120,
      render: (_, records) => {
        if (records.tags) {
          return (
            <ACResponsiveTags
              tags={records.tags}
              secondaryLabelDelimiter=":"
              secondaryTitle="Value"
              columnTitle="Key"
              onFilterClick={
                applyCellFilter
                  ? (tag) => {
                      const [key, ...values] = tag.split(":");
                      applyCellFilter("keyValue", {
                        key: `"${key}"`,
                        value: `"${values.join(":")}"`,
                      });
                    }
                  : undefined
              }
            />
          );
        }
        return CellRenderers.VALUE_OR_NA();
      },
      sorter: true,
    },
    {
      title: "Component Name",
      dataIndex: "componentName",
      key: "componentName",
      render: CellRenderers.VALUE_OR_NA,
      width: 150,
      sorter: true,
    },
    {
      title: "Component Affected Version",
      dataIndex: "componentVersion",
      key: "componentVersion",
      render: CellRenderers.VALUE_OR_NA,
      width: 200,
      sorter: true,
    },
    {
      title: "Component Fix Version",
      dataIndex: "componentFixVersions",
      key: "componentFixVersions",
      render: (text, record) => {
        const fixVersion = record.additionalDetails?.componentFixVersions;
        if (fixVersion) {
          return <ACResponsiveTags tags={fixVersion?.split(",")} />;
        }
        return CellRenderers.VALUE_OR_NA();
      },
      width: 180,
      sorter: true,
    },
    {
      title: "Fixable Using Tool",
      dataIndex: "fixability",
      key: "fixability",
      render: (value, record) =>
        renderBooleanValues(record.additionalDetails?.fixability),
      width: 150,
      sorter: true,
    },
    {
      title: "Exploit Maturity",
      dataIndex: "SNYK_exploitMaturity",
      key: "SNYK_exploitMaturity",
      render: (__, record) => {
        const value =
          record.additionalDetails?.SNYK_exploitMaturity ||
          record.additionalDetails?.exploitMaturity;
        return CellRenderers.VALUE_OR_NA(
          value
            ? _.startCase(_.toLower(value as unknown as string))
            : undefined,
        );
      },
      width: 150,
      sorter: true,
    },
    {
      title: "Repository",
      dataIndex: "repositoryName",
      key: "repositoryName",
      render: (__, record) => {
        const repositoryName = record.additionalDetails?.repositoryName;
        return repositoryName ? (
          record.additionalDetails?.repositoryUrl ? (
            <ACLink
              href={record.additionalDetails.repositoryUrl.toString()}
              target="_blank"
            >
              {repositoryName}
            </ACLink>
          ) : (
            repositoryName
          )
        ) : (
          CellRenderers.VALUE_OR_NA(repositoryName)
        );
      },
      width: 150,
      sorter: true,
    },
    {
      title: "File Name",
      dataIndex: "filePath",
      key: "filePath",
      render: (path) =>
        !_.isEmpty(path)
          ? _.map(_.split(path, ","), (_path) => (
              <TrimText content={_path} length={100} />
            ))
          : "N/A",
      width: 150,
      sorter: true,
    },
    {
      title: "Line Number",
      dataIndex: "lineNumber",
      key: "lineNumber",
      width: 120,
      sortType: "number",
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "Developer",
      dataIndex: "developer",
      key: "developer",
      render: CellRenderers.VALUE_OR_NA,
      width: 110,
      sorter: true,
    },
    {
      title: "Device",
      dataIndex: "vmHostName",
      key: "vmHostName",
      render: (text, record) => {
        const vmHostName = record.additionalDetails?.vmHostName;
        if (vmHostName) {
          return (
            <FilterableCell
              showFilter={!isAQLActive}
              filterKey="vmHostName"
              value={undefined}
              className={undefined}
              getFilterValue={undefined}
            >
              {vmHostName}
            </FilterableCell>
          );
        }
        return CellRenderers.VALUE_OR_NA();
      },
      width: 150,
      sorter: true,
    },
    {
      title: "URL/Endpoint",
      dataIndex: "newUrl",
      key: "newUrl",
      render: (url) => (
        <TrimText content={CellRenderers.VALUE_OR_NA(url)} length={100} />
      ),
      width: 250,
    },
    {
      title: "Image Name",
      dataIndex: "containerImageName",
      key: "containerImageName",
      render: (text, record) => {
        return CellRenderers.VALUE_OR_NA(getImageName(record));
      },
      width: 150,
      sorter: true,
    },
    {
      title: "Twistlock Origin Type",
      dataIndex: "Twistlock_originType",
      key: "Twistlock_originType",
      render: (_, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.originType),
      width: 160,
      sorter: true,
    },
    {
      title: "Twistlock Source",
      dataIndex: "Twistlock_scanType",
      key: "Twistlock_scanType",
      render: (_, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.type),
      width: 150,
      sorter: true,
    },
    {
      title: "AMC Rule",
      dataIndex: "ruleId",
      key: "ruleId",
      render: (_, record) => <ACResponsiveTags tags={record.ruleId} />,
      width: 130,
      sorter: true,
    },
    {
      title: "Compliance",
      dataIndex: "policy",
      key: "policy",
      width: 150,
      sorter: true,
      render: (_, record) => <ACResponsiveTags tags={record.policy} />,
    },
    {
      title: "Last Changed",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      width: 130,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastUpdated", value),
      dateFormatter: dateFormatter,
    },
    {
      title: `Last ${getVocabulary("mitigated")} Date`,
      dataIndex: "issueResolved",
      key: "issueResolved",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("issueResolved", value),
      dateFormatter: dateFormatter,
    },
    {
      title: "Exploit Available",
      dataIndex: "exploited",
      key: "exploited",
      render: (val, record) =>
        renderBooleanValues(record.additionalDetails?.exploited),
      width: 100,
      sorter: true,
    },
    {
      title: "XRay Path",
      dataIndex: "XRay_Path",
      key: "XRay_Path",
      render: (text, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.JFROG_XRAY_path),
      width: 150,
      sorter: true,
    },
    {
      title: "XRay Impacted Artifact",
      dataIndex: "XRay_Impacted_Artifact",
      key: "XRay_Impacted_Artifact",
      render: (text, record) =>
        CellRenderers.VALUE_OR_NA(
          record.additionalDetails?.JFROG_XRAY_impacted_artifact,
        ),
      width: 150,
      sorter: true,
    },
    {
      title: `Last ${getVocabulary("triage")} Date`,
      dataIndex: "lastTriagedDate",
      key: "lastTriagedDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastTriagedDate", value),
      dateFormatter: dateFormatter,
    },
    ...(featureFlags.RISK_SCORE_3_0
      ? ([
          {
            title: `${getVocabulary("assetScore")}`,
            dataIndex: "assetScore",
            key: "assetScore",
            width: 165,
            sorter: true,
            sortType: "number",
            render: (_, finding) => (
              <FindingAssetScoreFormula finding={finding} />
            ),
          },
        ] as FindingColumns)
      : []),
    {
      title: "IP Addresses",
      dataIndex: "vmHostIpAddresses",
      key: "vmHostIpAddresses",
      width: 165,
      render: (vmHostIpAddresses) => {
        if (!_.isEmpty(vmHostIpAddresses)) {
          return (
            <FilterableCell
              showFilter={!isAQLActive}
              filterKey="vmHostIpAddresses"
              value={undefined}
              className={undefined}
              getFilterValue={undefined}
            >
              {vmHostIpAddresses.join(",")}
            </FilterableCell>
          );
        }
        return CellRenderers.VALUE_OR_NA();
      },
      sorter: true,
    },
    {
      title: "CVSS Vector",
      dataIndex: "cvssVector",
      key: "cvssVector",
      width: 165,
      render: CellRenderers.VALUE_OR_NA,
    },
    {
      title: "EPSS Score",
      dataIndex: "epssProbability",
      key: "epssProbability",
      width: 165,
      sorter: true,
      sortType: "number",
      render: (value, record) => {
        return (
          <EPSSScore epssScore={record.additionalDetails?.epssProbability} />
        );
      },
    },
    {
      title: "EPSS Percentile",
      dataIndex: "epssScore",
      key: "epssScore",
      width: 165,
      sorter: true,
      sortType: "number",
      render: (value, record) => {
        return <EPSSScore epssScore={record.additionalDetails?.epssScore} />;
      },
    },
    {
      title: "CISA KEV",
      dataIndex: "isCISAKEV",
      key: "isCISAKEV",
      render: (val, record) =>
        renderBooleanValues(record.additionalDetails?.isCISAKEV),
      width: 100,
      sorter: true,
    },
    {
      title: "CISA Due Date",
      dataIndex: "cisaDueDate",
      key: "cisaDueDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (_, record) =>
        dateFormatter.render(
          "cisaDueDate",
          record.additionalDetails?.cisaDueDate as unknown as number,
        ),
      dateFormatter: dateFormatter,
    },
    {
      title: "CTI Score",
      dataIndex: "ctiScore",
      key: "ctiScore",
      width: 165,
      sorter: true,
      sortType: "number",
      render: (value, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.ctiScore),
    },
    {
      title: "Last Seen Date",
      dataIndex: "lastSeenDate",
      key: "lastSeenDate",
      width: 165,
      sorter: true,
      sortType: "date",
      render: (value) => dateFormatter.render("lastSeenDate", value),
      dateFormatter: dateFormatter,
    },
    ...(featureFlags.VERSIONS
      ? ([
          {
            title: "Versioned Findings Count",
            dataIndex: "versionFindingsCount",
            key: "versionFindingsCount",
            width: 165,
            sorter: true,
            sortType: "number",
            render: (value) =>
              formatNumber({
                count: CellRenderers.VALUE_OR_NA(value),
                onlyString: true,
                showTooltip: true,
              }),
          },
        ] as FindingColumns)
      : []),
    {
      title: `${getVocabulary("triage")} Due Date`,
      dataIndex: "triageDueDate",
      key: "triageDueDate",
      width: 165,
      render: (value) =>
        globalSettings.isSLAEnabled
          ? dateFormatter.render("triageDueDate", value)
          : NOT_AVAILABLE,
      dateFormatter: dateFormatter,
      sortType: "date",
      sorter: true,
    },
    {
      title: `${getVocabulary("remediation")} Due Date`,
      dataIndex: "remediationDueDate",
      key: "remediationDueDate",
      width: 165,
      render: (value) =>
        globalSettings.isSLAEnabled
          ? dateFormatter.render("remediationDueDate", value)
          : NOT_AVAILABLE,
      dateFormatter: dateFormatter,
      sortType: "date",
      sorter: true,
    },
    {
      title: "Status Reason",
      dataIndex: "statusChangeReason",
      key: "statusChangeReason",
      width: 165,
      sorter: true,
      render: (value, record) =>
        CellRenderers.VALUE_OR_NA(record.additionalDetails?.statusChangeReason),
    },
    {
      title: "Ticket Assignee",
      dataIndex: "ticketOwnerNames",
      key: "ticketOwnerNames",
      width: 165,
      sorter: true,
      render: (value, record) => {
        const ticketOwners = record.additionalDetails?.ticketOwnerNames || [];
        const filteredOwners = ticketOwners.filter(
          (ownerEmail: string) => ownerEmail,
        );
        if (filteredOwners && filteredOwners.length > 0) {
          return (
            <span className="d-flex">
              {filteredOwners.map((ownerEmail: string, index: number) => (
                <span key={index} className="d-flex">
                  <User userEmail={ownerEmail} />
                  {index < filteredOwners.length - 1 && <span>,&nbsp;</span>}
                </span>
              ))}
            </span>
          );
        }
        return CellRenderers.VALUE_OR_NA(filteredOwners);
      },
    },
    {
      title: `${getVocabulary("resolution")} SLA Status`,
      dataIndex: "resolutionSlaStatus",
      key: "resolutionSlaStatus",
      width: 165,
      render: (value, record) =>
        globalSettings.isSLAEnabled
          ? CellRenderers.VALUE_OR_NA(
              find(SLA_STATUS_FILTERS, { id: value ?? null })?.name,
            )
          : NOT_AVAILABLE,
    },
    {
      title: "Remediation Group",
      dataIndex: "correlationName",
      key: "correlationName",
      width: 165,
      render: (value: string, record: FindingSummary) =>
        CellRenderers.VALUE_OR_NA(
          _.uniq(
            _.map(record.correlationMetadata, (correlation: any) =>
              correlationNameRenderer(correlation.correlationName),
            ),
          ).join(", "),
        ),
    },
    {
      title: "Remediation Action",
      dataIndex: "remediationAction",
      key: "remediationAction",
      width: 165,
      render: (value: string, record: FindingSummary) =>
        CellRenderers.VALUE_OR_NA(
          _.uniq(
            _.map(
              record.correlationMetadata,
              (correlation: any) => correlation.remediationAction,
            ),
          ).join(", "),
        ),
    },
    {
      title: "Mobb Fix Available",
      dataIndex: "mobbFixUrl",
      key: "mobbFixUrl",
      width: 165,
      render: (value: string, record: FindingSummary) =>
        record.additionalDetails?.mobbFixUrl ? "True" : "False",
    },
    ...(featureFlags.CRA
      ? ([
          {
            title: "Exploit Status",
            dataIndex: ["compliance", "cra", "exploitStatus"],
            key: "craExploitStatus",
            sorter: true,
            sortType: "string" as const,
            render: (val: string) => {
              return val ?? getCRADefaultStatus("exploitStatus");
            },
            width: 120,
          },
          {
            title: "CRA Notification Status",
            dataIndex: ["compliance", "cra", "notificationStatus"],
            key: "craNotificationStatus",
            render: (val: string) => {
              return val ?? getCRADefaultStatus("notificationStatus");
            },
            width: 120,
            sorter: true,
            sortType: "string" as const,
          },
          {
            title: "CRA Early Warning Due",
            dataIndex: ["compliance", "cra", "earlyWarningDue"],
            key: "craEarlyWarningDue",
            render: (val: string) => {
              return dateFormatter.render("craEarlyWarningDue", val);
            },
            dateFormatter: dateFormatter,
            width: 120,
            sorter: true,
            sortType: "date" as const,
          },
          {
            title: "CRA Notification Due",
            dataIndex: ["compliance", "cra", "notificationDue"],
            key: "craNotificationDue",
            render: (val: string) => {
              return dateFormatter.render("craNotificationDue", val);
            },
            dateFormatter: dateFormatter,
            width: 120,
            sorter: true,
            sortType: "date" as const,
          },
          {
            title: "CRA Final Report Due",
            dataIndex: ["compliance", "cra", "finalReportDue"],
            key: "craFinalReportDue",
            render: (val: string) => {
              return dateFormatter.render("craFinalReportDue", val);
            },
            dateFormatter: dateFormatter,
            width: 120,
            sorter: true,
            sortType: "date" as const,
          },
          {
            title: "CRA User Disclosure Due",
            dataIndex: ["compliance", "cra", "userDisclosureDue"],
            key: "craUserDisclosureDue",
            render: (val: string) => {
              return dateFormatter.render("craUserDisclosureDue", val);
            },
            dateFormatter: dateFormatter,
            width: 120,
            sorter: true,
            sortType: "date" as const,
          },
          {
            title: "CRA User Disclosure Status",
            dataIndex: ["compliance", "cra", "userDisclosureStatus"],
            key: "craUserDisclosureStatus",
            render: (val: string) => {
              return val ?? getCRADefaultStatus("userDisclosureStatus");
            },
            width: 120,
            sorter: true,
            sortType: "string" as const,
          },
          {
            title: "CRA Remediation Notes",
            dataIndex: ["compliance", "cra", "remediationEvidence"],
            key: "craRemediationEvidence",
            render: (val: string) => {
              return val ? val : NOT_AVAILABLE;
            },
            width: 120,
          },
        ] as FindingColumns)
      : []),
  ];

  if (!isEmpty(customColumns)) {
    columns.push(...customColumns);
  }
  if (!isEmpty(customFieldColumns)) {
    columns.push(...customFieldColumns);
  }
  if (!isEmpty(derivedAttributesColumns)) {
    columns.push(...derivedAttributesColumns);
  }
  columns.forEach((column) => {
    if (
      GroupBySupportedFields.includes(column.key as string) &&
      isUndefined(column.allowGroupBy)
    ) {
      column.allowGroupBy = true;
    }
  });
  return columns;
}

const renderCVELink = (cve: string, findingId: number) => {
  return (
    <ACLink
      target="_blank"
      href={`/compliance/vulnerability-rules/${cve}/redirect?findingId=${findingId}`}
      primary={false}
    >
      {cve}
    </ACLink>
  );
};
const renderExceptionLink = (
  riskRegister: string | number,
  exceptionId: number,
) => {
  return (
    <InternalLink
      to={`/exceptions/${exceptionId}`}
      component={(props) => (
        <ACLink
          primary={false}
          {...props}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    >
      {riskRegister}
    </InternalLink>
  );
};

const renderCWELink = (cwe: string) => {
  return (
    <ACLink
      target="_blank"
      href={`https://cwe.mitre.org/data/definitions/${cwe.replace(
        "CWE-",
        "",
      )}.html`}
      primary={false}
    >
      {cwe}
    </ACLink>
  );
};

const renderException = (
  exceptionData: string[],
  record: FindingSummary,
  applyCellFilter?: ((key: string, id: string | number) => void) | boolean,
  supportLink = false,
  isDate = false,
) => {
  return (
    <ACResponsiveTags
      tags={compact(exceptionData)}
      itemRenderer={
        supportLink
          ? (exceptions: string | number) => {
              if (_.isString(exceptions)) {
                const riskRegisterID = _.find(
                  record.exceptions,
                  (data) => data.name === exceptions,
                )?.id;
                return renderExceptionLink(exceptions, riskRegisterID!);
              }
              return renderExceptionLink(exceptions, exceptions);
            }
          : isDate
            ? (date) => dayjs(date).format("MMMM DD, YYYY")
            : undefined
      }
      onFilterClick={
        isFunction(applyCellFilter)
          ? (riskRegister) => {
              const riskRegisterID = _.find(
                record.exceptions,
                (data) => data.name === riskRegister,
              )?.id;
              applyCellFilter("riskRegister", riskRegisterID!);
            }
          : undefined
      }
    />
  );
};
